/**
 * BoatLibrary.ts — runtime loader for boat designs.
 *
 * Boats are described by `BoatSpec` JSON files served from `/boats/`.
 * `BoatLibrary.load()` fetches an index.json that lists available designs
 * and resolves each into a baked `BoatHull` ready for `new Boat(hull)`.
 *
 * This keeps boat definitions as pure data: anyone can drop a new JSON
 * spec into `public/boats/` and reference it without recompiling.
 */

import {
  DEFAULT_WHEEL_GEAR_RATIO,
  BoatHull,
  BoatSpec,
  deriveHull,
} from "./BoatHull";

/**
 * Names matching this regex are treated as anchor-only nodes: their world
 * position is harvested for app-level use (camera, cleats, fenders…) and
 * any geometry attached to them is HIDDEN at render. The match is
 * case-insensitive and ignores `. _ -` separators, so authors can spell
 * `Skipper.Head`, `skipper_head`, `SkipperHead` etc.
 *
 * Patterns covered: skipperhead, helmsmanhead, cleat*, fender*, anchor*,
 * camera*, marker*, helper*, debug*, hidden*, gizmo*, empty*.
 */
const ANCHOR_NODE_PATTERN =
  /^(skipperhead|helmsmanhead|cleat|fender|anchor|camera|marker|helper|debug|hidden|gizmo|empty)/i;

function isAnchorNodeName(name: string): boolean {
  const norm = name.toLowerCase().replace(/[._\-\s]+/g, "");
  return ANCHOR_NODE_PATTERN.test(norm);
}

/** Manifest entry pointing at one boat spec JSON. */
export interface BoatLibraryEntry {
  /** Stable identifier (e.g. "antila_28_2"). */
  id: string;
  /** Human-readable display name. */
  name: string;
  /** URL relative to baseUrl, pointing at a `BoatSpec` JSON. */
  url: string;
}

/** Schema of `public/boats/index.json`. */
export interface BoatLibraryIndex {
  boats: BoatLibraryEntry[];
}

/** Loaded library: id → baked hull. */
export class BoatLibrary {
  private readonly hulls = new Map<string, BoatHull>();
  /**
   * Optional per-id scene-mode variant used only for the player boat. We
   * keep the *flat* (`hulls`) variant as the canonical one so neighbour
   * boats sharing the same id stay cheap; the animated copy is built only
   * when the spec asks for it via `mesh.sceneMode === true`.
   */
  private readonly animatedHulls = new Map<string, BoatHull>();
  private readonly entries = new Map<string, BoatLibraryEntry>();

  private constructor() {}

  /**
   * Fetch the index and every spec referenced by it.
   * `baseUrl` should end in '/' (e.g. "boats/").
   */
  static async load(baseUrl = "boats/"): Promise<BoatLibrary> {
    const lib = new BoatLibrary();
    const indexUrl = `${baseUrl}index.json`;
    const idx = await fetchJson<BoatLibraryIndex>(indexUrl);
    if (!idx || !Array.isArray(idx.boats)) {
      throw new Error(`BoatLibrary: malformed index at ${indexUrl}`);
    }
    await Promise.all(
      idx.boats.map(async (entry) => {
        const spec = await fetchJson<BoatSpec>(
          `${baseUrl}${entry.url.replace(/^boats\//, "")}`,
        );
        validateSpec(spec, entry.id);
        const hull = deriveHull(spec);
        // If the spec references an external mesh, load + bake it now so the
        // BoatRenderPass constructor can pick it up synchronously later.
        if (spec.mesh && spec.mesh.url) {
          try {
            const url = spec.mesh.url;
            const lower = url.toLowerCase();
            const isGlb = lower.endsWith(".glb");
            if (isGlb) {
              // Always load the flat single-mesh variant — every neighbour
              // boat reuses it, and `sceneMode` only upgrades the player.
              const { loadBoatGlb } = await import("../assets/GltfLoader");
              const loaded = await loadBoatGlb(url, {
                axisUp: spec.mesh.axisUp ?? "Y",
                axisLength: spec.mesh.axisLength ?? "Z",
                yawRad: ((spec.mesh.yawDeg ?? 0) * Math.PI) / 180,
                rollRad: ((spec.mesh.rollDeg ?? 0) * Math.PI) / 180,
                targetLOA: spec.LOA,
                yAlign: spec.mesh.yAlign ?? "raw",
                yOffset: spec.mesh.yOffset ?? 0,
                fallbackColor: hull.hullColor,
                overrideColor: spec.mesh.overrideColor
                  ? hull.hullColor
                  : undefined,
                flipNormals: spec.mesh.flipNormals ?? false,
                materialColors: spec.mesh.materialColors,
                hideNode: isAnchorNodeName,
                verbose: true,
              });
              hull.customMesh = loaded.mesh;
              hull.namedAnchors = loaded.anchors;
              applyHydroAnchors(hull, loaded.anchors, entry.id);

              if (spec.mesh.sceneMode) {
                // Build a *second* hull that carries the per-part scene so
                // the player's wheel/boom/rudder can animate. Neighbours
                // keep using `hull` above and stay a single draw call.
                const { loadBoatGlbScene } =
                  await import("../assets/GltfLoader");
                const scene = await loadBoatGlbScene(url, {
                  axisUp: spec.mesh.axisUp ?? "Y",
                  axisLength: spec.mesh.axisLength ?? "Z",
                  yawRad: ((spec.mesh.yawDeg ?? 0) * Math.PI) / 180,
                  rollRad: ((spec.mesh.rollDeg ?? 0) * Math.PI) / 180,
                  targetLOA: spec.LOA,
                  yOffset: spec.mesh.yOffset ?? 0,
                  flipNormals: spec.mesh.flipNormals ?? false,
                  fallbackColor: hull.hullColor,
                  materialColors: spec.mesh.materialColors,
                  verbose: true,
                });
                const animated: BoatHull = {
                  ...hull,
                  customMesh: undefined,
                  customScene: scene,
                  wheelGearRatio:
                    spec.mesh.wheelGearRatio ?? DEFAULT_WHEEL_GEAR_RATIO,
                  wheelAxis: spec.mesh.wheelAxis ?? "Y",
                  tillerGearRatio: spec.mesh.tillerGearRatio ?? 1.0,
                  tillerAxis: spec.mesh.tillerAxis ?? "Y",
                  rudderGearRatio: spec.mesh.rudderGearRatio ?? 1.0,
                  rudderAxis: spec.mesh.rudderAxis ?? "Y",
                  engineGearRatio: spec.mesh.engineGearRatio ?? 1.0,
                  engineAxis: spec.mesh.engineAxis ?? "Y",
                  centerboardAxis: spec.mesh.centerboardAxis ?? "X",
                  centerboardMaxAngleDeg:
                    spec.mesh.centerboardMaxAngleDeg ?? 80,
                };
                lib.animatedHulls.set(entry.id, animated);
              }
            } else {
              const { loadRhino3dm } = await import("../assets/Rhino3dmLoader");
              const mesh = await loadRhino3dm(url, {
                axisUp: spec.mesh.axisUp ?? "Z",
                yawRad: ((spec.mesh.yawDeg ?? 0) * Math.PI) / 180,
                rollRad: ((spec.mesh.rollDeg ?? 0) * Math.PI) / 180,
                targetLOA: spec.LOA,
                yOffset: spec.mesh.yOffset ?? 0,
                fallbackColor: hull.hullColor,
                overrideColor: hull.hullColor,
                verbose: true,
              });
              hull.customMesh = mesh;
            }
          } catch (err) {
            console.error(
              `BoatLibrary[${entry.id}]: failed to load mesh ${spec.mesh.url}, falling back to procedural`,
              err,
            );
          }
        }
        lib.hulls.set(entry.id, hull);
        lib.entries.set(entry.id, entry);
      }),
    );
    if (lib.hulls.size === 0) {
      throw new Error(`BoatLibrary: no boats loaded from ${indexUrl}`);
    }
    return lib;
  }

  /** Return the (flat) hull for `id`, or throw if unknown. */
  get(id: string): BoatHull {
    const h = this.hulls.get(id);
    if (!h) {
      throw new Error(
        `BoatLibrary: unknown boat id "${id}". Known: ${[...this.hulls.keys()].join(", ")}`,
      );
    }
    return h;
  }

  /**
   * Return the animated (scene-mode) hull for `id` if the spec opted in
   * via `mesh.sceneMode`. Falls back to the flat hull otherwise. Intended
   * for the *player* boat — neighbours should stick with {@link get}.
   */
  getAnimated(id: string): BoatHull {
    return this.animatedHulls.get(id) ?? this.get(id);
  }

  /** All loaded ids, in insertion order. */
  ids(): string[] {
    return [...this.hulls.keys()];
  }

  /** All entries (id + name + url) for UI listings. */
  list(): BoatLibraryEntry[] {
    return [...this.entries.values()];
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { cache: "no-cache" });
  if (!r.ok) {
    throw new Error(`fetch ${url} → HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

/**
 * Validate a `BoatSpec` enough to give a useful error before `deriveHull`
 * crashes with a NaN. Throws on missing or invalid fields.
 */
function validateSpec(spec: unknown, id: string): asserts spec is BoatSpec {
  if (!spec || typeof spec !== "object") {
    throw new Error(`BoatLibrary[${id}]: spec is not an object`);
  }
  const s = spec as Record<string, unknown>;
  const requiredNumbers = [
    "LOA",
    "B",
    "T",
    "D",
    "Cb",
    "cruiseSpeed",
    "turnRadiusAtCruise",
    "yawResponseTime",
    "enginePower",
    "windageArea",
    "GM",
  ] as const;
  for (const k of requiredNumbers) {
    if (typeof s[k] !== "number" || !Number.isFinite(s[k] as number)) {
      throw new Error(
        `BoatLibrary[${id}]: missing/invalid number field "${k}"`,
      );
    }
  }
  if (s.type !== "sailboat" && s.type !== "houseboat") {
    throw new Error(
      `BoatLibrary[${id}]: type must be "sailboat" or "houseboat", got ${JSON.stringify(s.type)}`,
    );
  }
  if (
    s.steering !== undefined &&
    s.steering !== "wheel" &&
    s.steering !== "tiller"
  ) {
    throw new Error(
      `BoatLibrary[${id}]: steering must be "wheel" or "tiller", got ${JSON.stringify(s.steering)}`,
    );
  }
}

/**
 * Convert `Buoyancy.*` and `Lateral.*` Empties (harvested from the glb's
 * named nodes) into runtime hydrodynamic data on the hull.
 *
 * Naming convention (case-insensitive, separators `_ . -` ignored when
 * matching the prefix):
 *
 *   Buoyancy.<station>.<side>
 *     side `C` → centerline sample, used once (Z forced to 0).
 *     side `Z` or `P` or any non-C → side sample, mirrored to the
 *     opposite side. Authors only need to place one side.
 *
 *   Lateral.<station>
 *     A single panel on the keel/centerline. Empties whose Y is well
 *     below water (Y < BOARD_DEPTH_THRESHOLD) are treated as the
 *     centerboard/keel and scale with `centerboardExtension`; shallower
 *     ones are treated as bare-hull side surface and stay active when
 *     the board is up.
 *
 * Buoyancy samples populate `hull.keelSamplePoints` (X, Z); the Y from
 * the Empty is currently informational only (vertical heave logic uses
 * a uniform `keelDepth` offset).
 *
 * Lateral panels populate `hull.lateralPanels`. The total area is split
 * across panels: deep (board) panels share `hull.lateral.area`; shallow
 * (hull) panels each get a small baseline `0.2 * area / Nshallow`. When
 * no deep panel is present, the whole area is shared by the shallow
 * panels.
 */
function applyHydroAnchors(
  hull: BoatHull,
  anchors: Record<string, [number, number, number]>,
  id: string,
): void {
  const BOARD_DEPTH_THRESHOLD = -0.4; // Y below this counts as "deep/board"
  const SIDE_EPS = 1e-3;

  // --- Buoyancy --------------------------------------------------------
  const buoy: Array<[number, number]> = [];
  for (const [key, pos] of Object.entries(anchors)) {
    const m = key.match(/^buoyancy[._\-]/i);
    if (!m) continue;
    const [x, , z] = pos;
    const parts = key.split(/[._\-]/);
    const side = (parts[parts.length - 1] ?? "").toUpperCase();
    if (side === "C") {
      buoy.push([x, 0]);
    } else {
      buoy.push([x, z]);
      if (Math.abs(z) > SIDE_EPS) {
        buoy.push([x, -z]);
      }
    }
  }
  if (buoy.length >= 3) {
    hull.keelSamplePoints = buoy;
  } else if (buoy.length > 0) {
    console.warn(
      `BoatLibrary[${id}]: only ${buoy.length} Buoyancy.* anchor(s) found, need ≥3 — falling back to procedural keel samples`,
    );
  }

  // --- Lateral panels --------------------------------------------------
  type RawPanel = { offsetX: number; offsetY: number; deep: boolean };
  const raw: RawPanel[] = [];
  for (const [key, pos] of Object.entries(anchors)) {
    if (!/^lateral[._\-]/i.test(key)) continue;
    const [x, y] = pos;
    raw.push({ offsetX: x, offsetY: y, deep: y < BOARD_DEPTH_THRESHOLD });
  }
  if (raw.length === 0) return;

  const baseArea = hull.lateral.area;
  if (baseArea <= 0) return;

  const deep = raw.filter((p) => p.deep);
  const shallow = raw.filter((p) => !p.deep);
  // Fraction of total lateral area assigned to bare-hull (shallow) panels.
  // The remainder goes to the deep/board panels. Both fractions sum to 1,
  // so the *total* effective area at full board extension equals the
  // hull's `lateral.area` (i.e. matches the spec). When the board is up,
  // only the shallow panels contribute, giving HULL_BASELINE_FRAC × area.
  const HULL_BASELINE_FRAC = 0.25;

  const panels: Array<{
    offsetX: number;
    offsetY: number;
    area: number;
    coupledToBoard: boolean;
  }> = [];

  if (deep.length > 0 && shallow.length > 0) {
    const aDeep = (baseArea * (1 - HULL_BASELINE_FRAC)) / deep.length;
    const aShallow = (baseArea * HULL_BASELINE_FRAC) / shallow.length;
    for (const p of deep) {
      panels.push({
        offsetX: p.offsetX,
        offsetY: p.offsetY,
        area: aDeep,
        coupledToBoard: hull.lateral.kind === "centerboard",
      });
    }
    for (const p of shallow) {
      panels.push({
        offsetX: p.offsetX,
        offsetY: p.offsetY,
        area: aShallow,
        coupledToBoard: false,
      });
    }
  } else if (deep.length > 0) {
    // Only deep panels authored — treat each as a full-area board panel.
    const aDeep = baseArea / deep.length;
    for (const p of deep) {
      panels.push({
        offsetX: p.offsetX,
        offsetY: p.offsetY,
        area: aDeep,
        coupledToBoard: hull.lateral.kind === "centerboard",
      });
    }
  } else {
    // Only shallow panels — split the whole area among them.
    const aShallow = baseArea / shallow.length;
    for (const p of shallow) {
      panels.push({
        offsetX: p.offsetX,
        offsetY: p.offsetY,
        area: aShallow,
        coupledToBoard: hull.lateral.kind === "centerboard",
      });
    }
  }

  hull.lateralPanels = panels;
  console.log(
    `BoatLibrary[${id}]: hydro anchors → ${buoy.length} buoyancy sample(s), ${panels.length} lateral panel(s) ` +
      `(deep=${deep.length}, shallow=${shallow.length}, totalA=${baseArea.toFixed(2)} m²)`,
  );
}
